import { Box, Typography } from '@mui/material';
import { getDocument } from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';
import '@/components/PdfViewer/pdfWorker';
import { PREVIEW_WIDTH } from './constants';

interface Props {
    file: File;
}

export default function PdfFirstPagePreview({ file }: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const objectUrl = URL.createObjectURL(file);

        async function renderFirstPage() {
            setIsLoading(true);

            try {
                const pdf = await getDocument({ url: objectUrl }).promise;
                const page = await pdf.getPage(1);
                const baseViewport = page.getViewport({ scale: 1 });
                const scale = PREVIEW_WIDTH / baseViewport.width;
                const viewport = page.getViewport({ scale });

                const canvas = canvasRef.current;
                if (!canvas || cancelled) return;

                canvas.height = viewport.height;
                canvas.width = viewport.width;

                await page.render({ canvas, viewport }).promise;
            } catch (error) {
                console.error('Failed to render PDF preview:', error);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        }

        void renderFirstPage();

        return () => {
            cancelled = true;
            URL.revokeObjectURL(objectUrl);
        };
    }, [file]);

    return (
        <Box
            sx={{
                position: 'relative',
                width: PREVIEW_WIDTH,
                minHeight: 200,
                borderRadius: '6px',
                overflow: 'hidden',
                bgcolor: '#f5f5f5',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {isLoading && (
                <Typography variant="caption" color="text.secondary">
                    Loading preview...
                </Typography>
            )}
            <Box
                component="canvas"
                ref={canvasRef}
                sx={{
                    display: isLoading ? 'none' : 'block',
                    width: '100%',
                    height: 'auto',
                }}
            />
        </Box>
    );
}
